import React from "react";
import { OcrTrendMonth } from "../../data/api/ocrQualityApi";

interface OcrAccuracyTrendChartProps {
  data: OcrTrendMonth[];
}

export const OcrAccuracyTrendChart: React.FC<OcrAccuracyTrendChartProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={headingStyle}>Accuracy Trend by Month</div>
        <div style={{ color: "#6b7280", fontSize: 14, padding: "32px 0", textAlign: "center" }}>
          No trend data available for the selected period.
        </div>
      </div>
    );
  }

  const maxAccuracy = 100;

  return (
    <div style={containerStyle}>
      <div style={headingStyle}>Accuracy Trend by Month</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <Legend color="#6366f1" label="Header Accuracy" />
        <Legend color="#10b981" label="GL Accuracy" />
        <Legend color="#f59e0b" label="Avg Fields Edited" />
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <Th>Month</Th>
              <Th>Docs</Th>
              <Th>Header Acc.</Th>
              <Th>GL Acc.</Th>
              <Th>Overall Acc.</Th>
              <Th>Zero-Edit %</Th>
              <Th>Avg Edits</Th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.month}>
                <Td>{row.month}</Td>
                <Td>{row.total_documents}</Td>
                <AccuracyTd value={row.avg_header_accuracy} max={maxAccuracy} color="#6366f1" />
                <AccuracyTd value={row.avg_gl_accuracy} max={maxAccuracy} color="#10b981" />
                <AccuracyTd value={row.avg_overall_accuracy} max={maxAccuracy} color="#3b82f6" />
                <AccuracyTd value={row.zero_edit_rate_pct} max={maxAccuracy} color="#8b5cf6" />
                <Td>{Number(row.avg_fields_edited).toFixed(1)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Legend: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#374151" }}>
    <span style={{ width: 12, height: 12, borderRadius: 2, background: color, display: "inline-block" }} />
    {label}
  </div>
);

const AccuracyTd: React.FC<{ value: number | string; max: number; color: string }> = ({
  value,
  max,
  color,
}) => {
  const num = Number(value);
  const pct = Math.min((num / max) * 100, 100);
  return (
    <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            width: 60,
            height: 6,
            background: "#f3f4f6",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              background: color,
              borderRadius: 3,
            }}
          />
        </div>
        <span style={{ fontSize: 13, color: "#374151" }}>{num.toFixed(1)}%</span>
      </div>
    </td>
  );
};

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th
    style={{
      padding: "8px 12px",
      textAlign: "left",
      fontSize: 12,
      fontWeight: 600,
      color: "#6b7280",
      borderBottom: "1px solid #e5e7eb",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <td
    style={{
      padding: "8px 12px",
      fontSize: 13,
      color: "#374151",
      borderBottom: "1px solid #f3f4f6",
    }}
  >
    {children}
  </td>
);

const containerStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  padding: "20px 24px",
};

const headingStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: "#111827",
  marginBottom: 16,
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};
